import { createExternalStore } from '@iheartjane/utils';
import { useCallback, useEffect, useSyncExternalStore } from 'react';

const { notify, subscribe } = createExternalStore<boolean>();

/** Dismissed on this page, so an intro stays closed when storage writes fail. */
const dismissed = new Set<string>();

/** Enabled, unseen intros in the order they mounted. Only the first is open. */
const waiting = new Set<string>();

const wasSeen = (storageKey: string): boolean => {
  if (dismissed.has(storageKey)) return true;
  try {
    return window.localStorage.getItem(storageKey) === 'true';
  } catch {
    return false;
  }
};

const persistSeen = (storageKey: string): void => {
  dismissed.add(storageKey);
  try {
    window.localStorage.setItem(storageKey, 'true');
  } catch {
    return;
  }
};

const release = (storageKey: string) => {
  if (waiting.delete(storageKey)) notify();
};

const isFirstWaiting = (storageKey: string) =>
  waiting.values().next().value === storageKey;

interface UseFeatureIntroArgs {
  /**
   * Whether the intro may show now, e.g. its flag is on and it's on the right page.
   * Keep it false while a blocking overlay such as the age gate is up.
   */
  enabled: boolean;
  /** localStorage key, one per feature. Include any per-menu prefix. */
  storageKey: string;
}

/**
 * Opens a `FeatureIntroModal` until the shopper dismisses it once. Pass
 * `close` to `onRequestClose`; it saves the dismissal and hides the intro.
 *
 * Only one intro is open at a time: the first enabled, unseen intro to mount
 * wins, and the next opens once it has closed. Always closed on the server
 * and during hydration.
 */
export const useFeatureIntro = ({
  enabled,
  storageKey,
}: UseFeatureIntroArgs) => {
  useEffect(() => {
    if (!enabled || wasSeen(storageKey)) return;

    waiting.add(storageKey);
    notify();

    return () => release(storageKey);
  }, [enabled, storageKey]);

  const isOpen =
    useSyncExternalStore(
      subscribe,
      () => isFirstWaiting(storageKey),
      () => false
    ) && enabled;

  const close = useCallback(() => {
    persistSeen(storageKey);
    release(storageKey);
  }, [storageKey]);

  return { close, isOpen };
};
